import { Widget } from "@foretagsplatsen/widgetjs";
import ArrayStream from "../../../list/streams/ArrayStream.js";
import ListSource from "../../../list/ListSource.js";

/**
 * Renders a list of items that can be manipulated using a ListSource
 * instance. The items are rendered by a "strategy" allowing for very
 * different presentations (e.g., cards or table).
 *
 * @param {ListSource} source - source of truth when it comes to
 *   fetching or manipulating the items
 * @param {AbstractListStrategy} strategy - Strategy used to render
 *   the loaded items
 */
export default class ListSourceWidget extends Widget {
	constructor({
		source = ListSource.makeAndFetch({ stream: new ArrayStream() }),
		strategy,
	}) {
		super(...arguments);

		this._strategy = strategy;
		this._items = null;
		this._onDataFetchRegistration = undefined;
		this._isMounted = false;

		this.setSource(source);
		strategy.setListSourceWidget(this);
	}

	orderBy(...args) {
		this._source.orderBy(...args);
		this._source.fetch();
	}

	getAscendingSort() {
		return this._source.getAscendingSort();
	}

	isCurrentSortCriterion(...args) {
		return this._source.isCurrentSortCriterion(...args);
	}

	setSource(source) {
		this._source = source;

		this._onDataFetchRegistration?.unbind();

		this._onDataFetchRegistration = source?.registerOnDataFetch(
			({ items, loading }) => {
				this._items = loading ? null : items;
				this.update();
			},
		);
	}

	setEmptySource() {
		this._items = null;

		this.setSource(
			ListSource.makeAndFetch({
				stream: new ArrayStream({}),
			}),
		);
	}

	searchBy(searchString) {
		this._source.searchBy(searchString);
		return this._source.fetch();
	}

	renderContentOn(html) {
		if (this._items === null) {
			this._renderLoadingOn(html);
			return;
		}

		if (this._items.length === 0) {
			this._renderEmptyOn(html);
			return;
		}

		this._renderLoadedContentOn(html);
	}

	makeItemRenderable(...args) {
		return this._strategy.makeItemRenderable(...args);
	}

	_renderLoadingOn(html) {
		html.div(
			{
				class: [this._cssClass(), "loading"],
				"data-test": this._dataTest(),
			},
			this._strategy.renderLoadingOn.bind(this._strategy),
		);
	}

	_renderEmptyOn(html) {
		html.div(
			{
				class: [this._cssClass(), "empty"],
				"data-test": this._dataTest(),
			},
			this._strategy.renderEmptyOn.bind(this._strategy),
		);
	}

	_renderLoadedContentOn(html) {
		html.div(
			{
				class: this._cssClass(),
				"data-test": this._dataTest(),
			},
			this._strategy.itemsRenderer({
				items: this._items,
			}),
		);
	}

	_cssClass() {
		return this._strategy.cssClass();
	}

	_dataTest() {
		return this._strategy.baseCss();
	}
}
